import React, {useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {FormProvider, useForm} from 'react-hook-form';
import ChekinProSettings from 'ChekinProSettings';
import {HIDEABLE_SECTIONS} from 'utils/embedded';
import {useGuest, useFetchTopOffers} from 'hooks';
import {useGetGuestId} from 'hooks/useGetGuestId';
import {useLayoutMode} from 'hooks/useLayoutMode';
import {useReservation} from 'context/reservation';
import {DEVICE, useScreenResize} from '@chekin/guestapp-ui-kit';
import {GUEST_FORM_NAMES, SDK_MODE, SessionStorage} from '@chekin/core';
import {STORAGE_KEYS} from 'utils/constants';
import {FormTypes} from 'utils/guestForm';
import {GuestFormFieldsWrapper} from 'components/GuestFormFields';
import {Page} from 'components/Page/Page';
import {AutoFill} from './AutoFill';
import {WhyDataRequired} from 'components/WhyDataRequired';
import {FormWrapper, Title, Wrapper} from './styled';

function AddPersonalDataForm() {
  const {t} = useTranslation();
  const {isMatch: isMobileMode} = useScreenResize(DEVICE.laptop);
  const [errorModal, setErrorModal] = React.useState(false);
  const isValidateKeyboard = isMobileMode && errorModal;
  const guestId = useGetGuestId();
  const {guest} = useGuest({id: guestId});
  const layoutMode = useLayoutMode();
  const {data: reservation} = useReservation();

  useFetchTopOffers({
    reservationId: reservation?.id,
  });
  const methods = useForm({
    shouldFocusError: !isValidateKeyboard,
    defaultValues: {
      [GUEST_FORM_NAMES.residence_city]: null,
      [GUEST_FORM_NAMES.birth_place_city]: null,
      [GUEST_FORM_NAMES.document_expedition_city]: null,
    },
  });
  const hasPersistedData = Boolean(
    SessionStorage.get<FormTypes>(STORAGE_KEYS.personalFormData),
  );
  const isAutofillHidden = ChekinProSettings.hiddenSections?.includes(
    HIDEABLE_SECTIONS.autofill,
  );
  const isAutoFillingAvailable = !hasPersistedData && !guest?.id && !isAutofillHidden;
  const isSubHeaderVisible =
    ChekinProSettings.mode !== SDK_MODE.onlyGuestForm && layoutMode === 'default';

  const navbarTitle = useMemo(() => {
    if (layoutMode === 'default') {
      return isMobileMode ? t('guest_data') : t('online_chekin');
    }
    return t('guest_data');
  }, [layoutMode, t, isMobileMode]);

  return (
    <Page
      isSubHeaderVisible={isSubHeaderVisible}
      title={t('guest_data')}
      isBackButtonDisabled
      navBarTitle={navbarTitle}
      className="guest-form kiosko:pt-[66px]"
    >
      {layoutMode === 'kiosko' && <WhyDataRequired />}
      <Wrapper className="guest-form__header kiosko:hidden">
        <Title className="guest-form__title">{t('enter_your_personal_data')}</Title>
        {isAutoFillingAvailable && <AutoFill />}
      </Wrapper>
      <FormProvider {...methods}>
        <FormWrapper>
          <GuestFormFieldsWrapper setErrorModal={setErrorModal} />
        </FormWrapper>
      </FormProvider>
    </Page>
  );
}

export {AddPersonalDataForm};
